import {useEffect, useState, type FormEvent} from 'react'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {isAxiosError} from 'axios'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {Label} from '@/components/ui/label'
import {DataTable, type Column} from '@/components/data-table'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {KairosMark} from '@/components/kairos-mark'
import {PageHeading} from '@/components/page-heading'
import {ListEmptyState} from '@/components/section-card'
import {HelpIcon} from '@/components/help-icon'
import {NoAccessState} from '@/components/no-access-state'
import {FreeProColumns} from '@/components/plan-value-items'
import {useBusiness} from '@/hooks/empresa/organizacion/use-businesses'
import {useAuth} from '@/hooks/platform/use-auth'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useSolicitudSst, useSolicitarSst} from '@/hooks/inci/consola/use-solicitud-sst'
import {translateApiError} from '@/lib/errors'
import {AutoDebitoSection} from '@/components/billing/auto-debito-section'
import {ActivatePlanBlock} from '@/components/billing/activate-plan-block'
import {CycleToggleSection} from '@/components/billing/cycle-toggle-section'
import {PrecioAcordadoTag} from '@/components/billing/precio-acordado'
import {
  useBillingState,
  useBillingInvoices,
  useRemintInvoiceLink,
  usePutPaymentSource,
  useDeletePaymentSource,
  type BillingInvoice,
  type BillingSubscriptionEstado,
} from '@/hooks/inci/cartera/use-billing'
import {formatCopFull} from '@/lib/money'
import {formatDate, formatDateOnly} from '@/lib/risk'

const SUBSCRIPTION_TONE: Record<BillingSubscriptionEstado, 'green' | 'amber' | 'red' | 'neutral'> = {
  activa: 'green',
  pendiente_pago: 'amber',
  en_gracia: 'amber',
  degradada: 'red',
  cancelada: 'neutral',
}

const INVOICE_TONE: Record<BillingInvoice['estado'], 'green' | 'amber' | 'red' | 'neutral'> = {
  pagada: 'green',
  emitida: 'amber',
  vencida: 'red',
  anulada: 'neutral',
}

/** Sin suscripción (o degradada) la tarjeta es el bloque de activación; el sello de suscripción solo existe si hay una. */
function PlanCard({
  businessId,
  state,
  canEdit,
  defaultEmail,
}: {
  businessId: string
  state: NonNullable<ReturnType<typeof useBillingState>['data']>
  canEdit: boolean
  defaultEmail: string
}) {
  const {t} = useTranslation()
  const [verIncluye, setVerIncluye] = useState(false)
  const subscription = state.subscription
  const canActivate = !subscription || subscription.estado === 'degradada'

  if (canActivate && canEdit) {
    return (
      <ActivatePlanBlock
        businessId={businessId}
        state={state}
        defaultEmail={defaultEmail}
        reactivate={subscription?.estado === 'degradada'}
      />
    )
  }

  const price = subscription
    ? subscription.billing_cycle === 'mensual'
      ? state.price_mensual
      : state.price_anual
    : null

  return (
    <div className="surface space-y-3 p-5" data-testid="billing-plan-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-base text-foreground">{t('planFacturacion.currentPlanTitle')}</h2>
        <div className="flex items-center gap-2">
          <Tag tone="neutral" data-testid="billing-plan-tier">
            {t(`plans.tier.${state.plan_tier}`)}
          </Tag>
          {subscription && (
            <span className="flex flex-wrap items-center gap-2" data-testid="billing-status">
              <Tag tone={SUBSCRIPTION_TONE[subscription.estado]}>
                {t(`planFacturacion.status.${subscription.estado}`)}
              </Tag>
            </span>
          )}
        </div>
      </div>

      {subscription && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>{t(`planFacturacion.cycle.${subscription.billing_cycle}`)}</span>
          <span>
            {t('planFacturacion.periodLine', {
              start: formatDateOnly(subscription.period_start),
              end: formatDateOnly(subscription.period_end),
            })}
          </span>
          {price && (
            <span className="font-mono tabular-nums" data-testid="billing-plan-price">
              {formatCopFull(price.amount_cop + Math.round((price.amount_cop * price.iva_pct) / 100))}
            </span>
          )}
          <PrecioAcordadoTag price={price} />
          {subscription.estado === 'en_gracia' && (
            // Los días de gracia son fecha de negocio (`bogota_today`): se muestra el `period_end` del backend.
            <span className="flex items-center gap-1.5" data-testid="billing-grace-note">
              {t('planFacturacion.graceUntil', {end: formatDateOnly(subscription.period_end)})}
              <HelpIcon label={t('planFacturacion.datosIntactosLabel')} testId="billing-grace-porque">
                {t('planFacturacion.datosIntactos')}
              </HelpIcon>
            </span>
          )}
        </div>
      )}

      {subscription && (
        <div className="border-t border-border pt-3">
          <TenantAutoDebito businessId={businessId} state={state} canEdit={canEdit} />
        </div>
      )}

      {canEdit && subscription && (
        <PlanPrimaryAction
          businessId={businessId}
          subscription={subscription}
          hasPaymentSource={!!state.payment_source}
        />
      )}

      {state.plan_tier === 'paid' && (
        <div className="border-t border-border pt-3">
          <Button variant="ghost" size="sm" data-testid="billing-value-expand" onClick={() => setVerIncluye((v) => !v)}>
            {t(verIncluye ? 'planFacturacion.value.collapse' : 'planFacturacion.value.expand')}
          </Button>
          {verIncluye && (
            <div className="mt-3" data-testid="billing-value-block">
              <FreeProColumns />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Sin factura aún (la emite el barrido diario, `core/billing.py::open_tenant_subscription`) no hay enlace que pedir.
 * Con cobro automático no se pide el enlace solo: ofrecer «Pagar ahora» invitaría a pagar dos veces. */
function PlanPrimaryAction({
  businessId,
  subscription,
  hasPaymentSource,
}: {
  businessId: string
  subscription: NonNullable<NonNullable<ReturnType<typeof useBillingState>['data']>['subscription']>
  hasPaymentSource: boolean
}) {
  const {t} = useTranslation()
  const [gatewayUnconfigured, setGatewayUnconfigured] = useState(false)
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null)
  const remintLink = useRemintInvoiceLink(businessId)

  const awaitingPayment = subscription.estado === 'pendiente_pago' || subscription.estado === 'en_gracia'
  const {data: invoices} = useBillingInvoices(awaitingPayment ? businessId : undefined, 1)
  const pendingInvoice = invoices?.items.find((invoice) => invoice.estado !== 'pagada' && invoice.estado !== 'anulada')

  useEffect(() => {
    if (hasPaymentSource || !pendingInvoice || pendingInvoice.payment_url) return
    remintLink.mutate(pendingInvoice.id, {
      onSuccess: (data) => setPaymentUrl(data.payment_url),
      onError: (err) => {
        if (isAxiosError(err) && err.response?.status === 503) setGatewayUnconfigured(true)
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reruns only when the pending invoice id changes, not on every remintLink identity churn
  }, [hasPaymentSource, pendingInvoice?.id, pendingInvoice?.payment_url])

  if (subscription.estado === 'activa') {
    return null
  }

  if (!awaitingPayment) return null

  const payUrl = pendingInvoice?.payment_url ?? paymentUrl
  const cobroEnCurso = hasPaymentSource && !payUrl && !!pendingInvoice
  const hint = gatewayUnconfigured
    ? t('planFacturacion.gatewayUnconfigured')
    : !pendingInvoice
      ? t('planFacturacion.sinFacturaAunHint')
      : cobroEnCurso
        ? t('planFacturacion.cobroAutomaticoEnCurso')
        : payUrl
          ? null
          : t('planFacturacion.payNowNoLinkHint')

  return (
    <div
      className={`flex flex-wrap items-center gap-3 ${hint ? 'justify-between' : 'justify-end'}`}
      data-testid="billing-pay-now-block">
      {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
      {payUrl ? (
        <Button asChild data-testid="billing-pay-now-cta">
          <a href={payUrl} target="_blank" rel="noreferrer">
            {t('planFacturacion.cta.payNow')}
          </a>
        </Button>
      ) : (
        pendingInvoice && (
          <Button
            variant={cobroEnCurso ? 'outline' : 'default'}
            data-testid="billing-pay-now-retry"
            disabled={remintLink.isPending}
            onClick={() => {
              setGatewayUnconfigured(false)
              remintLink.mutate(pendingInvoice.id, {
                onSuccess: (data) => setPaymentUrl(data.payment_url),
                onError: (err) => {
                  if (isAxiosError(err) && err.response?.status === 503) setGatewayUnconfigured(true)
                },
              })
            }}>
            {remintLink.isPending
              ? t('common.saving')
              : t(cobroEnCurso ? 'planFacturacion.cta.linkInstead' : 'planFacturacion.cta.retryLink')}
          </Button>
        )
      )}
    </div>
  )
}

/** Solo para la facturación propia del tenant: un tenant de partner nunca llega acá. */
function TenantAutoDebito({
  businessId,
  state,
  canEdit,
}: {
  businessId: string
  state: NonNullable<ReturnType<typeof useBillingState>['data']>
  canEdit: boolean
}) {
  const {user} = useAuth()
  const put = usePutPaymentSource(businessId)
  const remove = useDeletePaymentSource(businessId)

  return (
    <AutoDebitoSection
      variant="inline"
      source={state.payment_source}
      wompi={state.wompi}
      defaultEmail={user?.email ?? ''}
      canEdit={canEdit}
      putPending={put.isPending}
      deletePending={remove.isPending}
      nextChargeAt={state.subscription?.period_end ?? null}
      onSave={(payload) => put.mutateAsync(payload)}
      onRemove={() => remove.mutateAsync()}
    />
  )
}

function InvoiceLines({lines}: {lines: BillingInvoice['lines']}) {
  const {t} = useTranslation()
  if (lines.length === 0) return null
  return (
    <ul className="mt-1 space-y-0.5" data-testid="billing-invoice-lines">
      {lines.map((line) => (
        <li key={line.id} className="flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
          <span className="min-w-0 truncate">{line.concepto}</span>
          {line.total_cop === 0 ? (
            <span className="shrink-0 font-mono tabular-nums">{t('planFacturacion.invoices.incluido')}</span>
          ) : (
            <span className="shrink-0 font-mono tabular-nums">{formatCopFull(line.total_cop)}</span>
          )}
        </li>
      ))}
    </ul>
  )
}

function InvoicesTable({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const {data, isLoading} = useBillingInvoices(businessId, page)

  const columns: Column<BillingInvoice>[] = [
    {
      key: 'numero',
      header: t('planFacturacion.invoices.numero'),
      width: 'w-44',
      cell: (row) => (
        <div>
          <span className="font-mono text-sm font-medium text-foreground">{row.numero_factura}</span>
          <InvoiceLines lines={row.lines} />
        </div>
      ),
    },
    {
      key: 'fecha',
      header: t('planFacturacion.invoices.fecha'),
      width: 'w-40',
      cell: (row) => <span className="font-mono text-xs text-muted-foreground">{formatDate(row.created_at)}</span>,
    },
    {
      key: 'estado',
      header: t('planFacturacion.invoices.estado'),
      width: 'w-28',
      cell: (row) => <Tag tone={INVOICE_TONE[row.estado]}>{t(`planFacturacion.invoiceEstado.${row.estado}`)}</Tag>,
    },
    {
      key: 'total',
      header: t('planFacturacion.invoices.total'),
      width: 'w-32',
      className: 'text-right',
      cell: (row) => (
        <span className="font-mono text-sm text-foreground tabular-nums">{formatCopFull(row.total_cop)}</span>
      ),
    },
  ]

  if (isLoading && !data) return <Skeleton className="h-48 w-full" />

  const rows = data?.items ?? []
  return (
    <div className="space-y-2">
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        emptyState={<ListEmptyState title={t('planFacturacion.invoices.empty')} />}
      />
      {data && data.total > 0 && (
        <PaginationBar page={page} pageSize={data.page_size} total={data.total} onPageChange={setPage} />
      )}
    </div>
  )
}

/** Kairos nunca revela a su cliente el precio de un partner: solo quién lo gestiona. */
function PartnerManagedNotice({partnerName}: {partnerName: string | null}) {
  const {t} = useTranslation()
  return (
    <div
      className="surface-raised mx-auto flex max-w-lg flex-col items-center gap-3 p-8 text-center"
      data-testid="billing-partner-managed">
      <KairosMark className="text-primary" />
      <h1 className="font-heading text-xl text-foreground">
        {partnerName
          ? t('planFacturacion.partnerManagedTitleNamed', {partner: partnerName})
          : t('planFacturacion.partnerManagedTitle')}
      </h1>
      <p className="text-sm text-muted-foreground">{t('planFacturacion.partnerManagedBody')}</p>
    </div>
  )
}

/** Autorado solo desde los gates reales de `core/plan.py`, nunca inventado. */
function ValueBlock() {
  return (
    <div className="surface p-5" data-testid="billing-value-block">
      <FreeProColumns />
    </div>
  )
}

/** Reusa la cola `SolicitudSst` (409 mientras hay una pendiente); los tenants de partner no la ven. */
function ServicioSstSection({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {data: solicitud} = useSolicitudSst(businessId, true)
  const solicitar = useSolicitarSst(businessId)
  const [open, setOpen] = useState(false)
  const [nota, setNota] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Los tres estados: mirar solo «pendiente» borraba la señal apenas Inci tomaba la solicitud.
  const pendiente = solicitud?.estado === 'pendiente'
  const atendida = solicitud?.estado === 'atendida'
  const descartada = solicitud?.estado === 'descartada'

  function close() {
    setOpen(false)
    setNota('')
    setError(null)
    solicitar.reset()
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    solicitar.mutate(nota.trim() || null, {onSuccess: close, onError: (err) => setError(translateApiError(err))})
  }

  return (
    <div className="surface space-y-3 p-5" data-testid="billing-servicio-sst">
      <div className="flex items-center gap-1.5">
        <h2 className="font-heading text-base text-foreground">{t('planFacturacion.servicioSst.title')}</h2>
        <HelpIcon label={t('planFacturacion.servicioSst.alcanceTipLabel')} testId="billing-servicio-sst-tip">
          {t('planFacturacion.servicioSst.alcanceTip')}
        </HelpIcon>
      </div>
      {pendiente ? (
        <div className="flex items-center gap-2" data-testid="billing-servicio-sst-pendiente">
          <Tag tone="amber">{t('planFacturacion.servicioSst.pendingTitle')}</Tag>
        </div>
      ) : open ? (
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="servicio-sst-nota">{t('planFacturacion.servicioSst.notaLabel')}</Label>
            <Textarea
              id="servicio-sst-nota"
              data-testid="servicio-sst-nota"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              maxLength={300}
              minRows={2}
            />
          </div>
          {error && (
            <p className="text-sm text-destructive" data-testid="servicio-sst-error">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={solicitar.isPending} data-testid="billing-servicio-sst-submit">
              {solicitar.isPending ? t('common.saving') : t('planFacturacion.servicioSst.submit')}
            </Button>
          </div>
        </form>
      ) : (
        // El backend solo rechaza una duplicada mientras está pendiente, así que el CTA sigue disponible.
        <div className="space-y-2">
          {atendida && (
            <div className="flex items-center gap-2" data-testid="billing-servicio-sst-atendida">
              <Tag tone="enCurso">{t('planFacturacion.servicioSst.atendidaTitle')}</Tag>
            </div>
          )}
          {descartada && (
            <div className="flex items-center gap-2" data-testid="billing-servicio-sst-descartada">
              <Tag tone="neutral">{t('planFacturacion.servicioSst.descartadaTitle')}</Tag>
            </div>
          )}
          <Button variant="outline" data-testid="billing-servicio-sst-cta" onClick={() => setOpen(true)}>
            {t(atendida || descartada ? 'planFacturacion.servicioSst.ctaDeNuevo' : 'planFacturacion.servicioSst.cta')}
          </Button>
        </div>
      )}
    </div>
  )
}

export function PlanFacturacionPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {data: business, isLoading: businessLoading} = useBusiness(businessId)
  const {user} = useAuth()
  const godReadOnly = useGodModeReadOnly()
  const isOwner = business?.role === 'owner'
  const canEdit = isOwner && !godReadOnly
  const {data: state, isLoading: stateLoading} = useBillingState(isOwner ? businessId : undefined)

  if (businessLoading) return <Skeleton className="h-40 w-full" />
  if (!business) return <NoAccessState />
  if (!isOwner) return <NoAccessState />
  if (stateLoading || !state) return <Skeleton className="h-60 w-full" />

  if (state.managed_by_partner) return <PartnerManagedNotice partnerName={state.partner_name} />

  return (
    <div className="space-y-6" data-testid="plan-facturacion-page">
      {!canEdit && (
        <div
          className="rounded-lg bg-secondary px-4 py-3 text-sm text-muted-foreground"
          data-testid="billing-readonly-banner">
          {t('business.readOnlyBanner')}
        </div>
      )}
      <PageHeading title={t('planFacturacion.title')} subtitle={business.name} />

      <Tabs defaultValue="plan" data-testid="plan-facturacion-tabs">
        <TabsList>
          <TabsTrigger value="plan" data-testid="plan-facturacion-tab-plan">
            {t('planFacturacion.tabs.plan')}
          </TabsTrigger>
          <TabsTrigger value="facturas" data-testid="plan-facturacion-tab-facturas">
            {t('planFacturacion.tabs.facturas')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="plan" className="space-y-6 pt-4">
          {state.plan_tier === 'free' && <ValueBlock />}

          {businessId && (
            <PlanCard businessId={businessId} state={state} canEdit={canEdit} defaultEmail={user?.email ?? ''} />
          )}

          {businessId && state.subscription?.estado === 'activa' && (
            <CycleToggleSection businessId={businessId} canEdit={canEdit} />
          )}

          {businessId && <ServicioSstSection businessId={businessId} />}
        </TabsContent>

        <TabsContent value="facturas" className="pt-4">
          {businessId && <InvoicesTable businessId={businessId} />}
        </TabsContent>
      </Tabs>
    </div>
  )
}
